import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const MerchandiseTable = ({ merchandise, onStockUpdate }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const navigate = useNavigate();

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'apparel', label: 'Apparel' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'collectibles', label: 'Collectibles' },
    { value: 'food', label: 'Food & Beverage' }
  ];

  const getStockStatus = (stock, lowStockThreshold = 10) => {
    if (stock === 0) {
      return { label: 'Out of Stock', color: 'bg-error text-error-foreground' };
    } else if (stock <= lowStockThreshold) {
      return { label: 'Low Stock', color: 'bg-warning text-warning-foreground' };
    } else {
      return { label: 'In Stock', color: 'bg-success text-success-foreground' };
    }
  };

  const filteredMerchandise = merchandise?.filter(item => {
    const matchesSearch = item?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         item?.sku?.toLowerCase()?.includes(searchTerm?.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || item?.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm">
      <div className="p-6 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-foreground">Merchandise Inventory</h3>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => navigate('/create-merchandise')}
          >
            Add Item
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 mt-4">
          <div className="flex-1">
            <Input
              type="search"
              placeholder="Search by name or SKU..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
            />
          </div>
          <div>
            <Select
              options={categoryOptions}
              value={categoryFilter}
              onChange={setCategoryFilter}
              placeholder="Filter by category"
              className="w-48"
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Item</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">SKU</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Price</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Stock</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Sales</th>
              <th className="text-left p-4 text-sm font-medium text-muted-foreground">Status</th>
              <th className="text-right p-4 text-sm font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredMerchandise?.map((item) => {
              const stockStatus = getStockStatus(item?.stock);
              return (
                <tr key={item?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={item?.image}
                          alt={item?.name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = '/assets/images/no_image.png';
                          }}
                        />
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground">{item?.name}</h4>
                        <p className="text-sm text-muted-foreground capitalize">{item?.category}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className="font-mono text-sm text-foreground">{item?.sku}</span>
                  </td>
                  <td className="p-4">
                    <span className="text-sm font-medium text-foreground">${item?.price}</span>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{item?.stock}</p>
                      <p className="text-sm text-muted-foreground">units</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <div>
                      <p className="text-sm font-medium text-foreground">{item?.totalSales}</p>
                      <p className="text-sm text-muted-foreground">${(item?.totalSales * item?.price)?.toLocaleString()}</p>
                    </div>
                  </td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${stockStatus?.color}`}>
                      {stockStatus?.label}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end space-x-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Package"
                        onClick={() => onStockUpdate(item?.id)}
                        disabled={item?.stock === 0}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="Edit"
                        onClick={() => navigate(`/edit-merchandise/${item?.id}`)}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        iconName="MoreHorizontal"
                        onClick={() => {}}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filteredMerchandise?.length === 0 && (
        <div className="p-8 text-center">
          <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h4 className="text-lg font-medium text-foreground mb-2">No merchandise found</h4>
          <p className="text-muted-foreground mb-4">
            {searchTerm || categoryFilter !== 'all' ?'Try adjusting your search or filter criteria' :'Add merchandise items to start selling at your events'
            }
          </p>
          <Button
            variant="default"
            iconName="Plus"
            iconPosition="left"
            onClick={() => navigate('/create-merchandise')}
          >
            Add Item
          </Button>
        </div>
      )}
    </div>
  );
};

export default MerchandiseTable;